/**
 * desk-tape — the shared contract between the chain Social SERVER route and the
 * INTERCEPTS client components for THE DESK READ + THE TAPE (v3, 2026-06-12).
 *
 * One `DeskTapeDay` = one published daily recap (`ecosystem_recaps`), reduced to
 * the fields the Social page needs: the desk's conclusion of that day (title +
 * summary, verbatim), its sentiment, and the provenance counts. The newest entry
 * is THE DESK READ (the page's lead); the rest are the scrubbable record.
 *
 * Plain JSON only — this crosses the RSC boundary as a prop.
 */

export interface DeskTapeDay {
  /** Calendar day the read covers (YYYY-MM-DD, UTC) — recap.date. */
  date: string;
  /** The desk's headline conclusion for the day — recap.title, verbatim. */
  title: string;
  /** The one-paragraph read — recap.summary, verbatim. */
  summary: string;
  /** Day mood — recap.sentiment ('bullish' | 'bearish' | 'mixed' | 'neutral'). */
  sentiment?: string;
  /** Structured highlight bullets (recap.highlights) — carry the @handle tokens
   *  the client resolves into "named in the read" focus chips. */
  highlights: string[];
  /** Active tracked accounts that day — recap.sourceCounts.activityPulses (tape bar height). */
  pulses: number;
  /** Breaking items folded into the read — recap.sourceCounts.breakingNews. */
  breaking: number;
  /** How many intercepts the desk featured in the brief — recap.featuredTweets.length. */
  featuredCount: number;
  /** ISO timestamp the read was published (recap.publishedAt), when known. */
  publishedAt?: string;
  /**
   * Which ecosystem the recap covers. ABSENT on the ~110 pre-multichain docs, which are the
   * default chain's — always read through `recapChainOf`. Carried so "Read the full brief"
   * links at the recap's OWN address instead of assuming the bare `/daily/<date>` one.
   */
  chain?: string;

  /**
   * THE SOCIAL DESK READ (optional overlay). When a `desk_reads/{chainId}_{date}`
   * doc exists for this day, the server overlays it here and the band renders the
   * SYNTHESIZED read instead of the recap headline/summary. When absent (the common
   * case until the pipeline is enabled), `read` is undefined and the band falls back
   * BYTE-FOR-BYTE to the recap-quoting behavior above. The recap fields are always
   * carried so "Read the full brief →" still links out.
   */
  read?: {
    /** The one-paragraph desk read (desk_reads.read), verbatim. */
    text: string;
    /** desk_reads.sentiment — replaces the recap sentiment when present. */
    sentiment?: string;
    /** desk_reads.notable — distinctive accounts ({handle, why}), max 3. */
    notable: Array<{ handle: string; why: string }>;
    /** desk_reads.inputStats — drives the provenance microline when present. */
    inputStats?: { tweetCount?: number; pulseCount?: number; topRating?: number };
    /** desk_reads.generatedAt (ISO) — the SYNTHESIZED dateline timestamp. */
    generatedAt?: string;
  };
}

/** Extract unique @handle tokens (lowercased, no @) from recap highlight bullets. */
export function handleTokensFromHighlights(highlights: string[]): string[] {
  const seen = new Set<string>();
  for (const h of highlights) {
    for (const m of h.matchAll(/@([A-Za-z0-9_]{2,30})/g)) {
      seen.add(m[1].toLowerCase());
    }
  }
  return Array.from(seen);
}
